import { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import TextareaAutosize from 'react-textarea-autosize';
import { QRCodeCanvas } from 'qrcode.react';
import jsQR from 'jsqr';
import { doSomethingWithEncryptedMnemonics } from './keysAndSigning';
import { useGlobalStateSelector, words } from './globalState';
import { devLog, adjustFloat, localTime } from './utils';
import iconCopy from './content_copy_FILL0_wght400_GRAD0_opsz48.svg';

// クリップボードコピーボタン
function ClipboardCopyButton(props) {
  const text = props.text;
  const language = useGlobalStateSelector(({ state }) => state.language ?? 'english');
  const dispatch = useGlobalStateSelector(({ dispatch }) => dispatch);

  return (
    <button className='button1' tabIndex='0'
      onClick={ async () => {
        await navigator.clipboard.writeText(text);

        dispatch({
          type: 'setNotification',
          key: 'notification',
          value: words.copiedToClipboard[language],
        });
      }}
    >
      <img className='size2x2' src={iconCopy} alt='copy text' />
    </button>
  );
}

// GENERAL LIST
function GeneralList(props) {
  const { state } = useGlobalStateSelector(({ state }) => ({ state }));
  const body = state.popup[props.popupLayer].body;

  return (
    <div className=''>
      {/* PC */}
      <div className='visibleMiddleOrMore flexColumn alignItemsCenter'>
        <div className='flexColumn alignItemsCenter'>
          { body.title }
        </div>
        <div className='flexColumn alignItemsCenter'>
          { body.options }
        </div>
      </div>
      {/* SP */}
      <div className='visibleSmallOrLess flexColumn widthMax'>
        <div className='flexColumn alignItemsCenter widthMax'>
          { body.title }
        </div>
        <div className='flexColumn alignItemsCenter widthMax'>
          { body.options }
        </div>
      </div>
    </div>
  );
}

// GENERAL ITEMS ; type: 'generalItems'
function GeneralItems(props) {
  const popup = useGlobalStateSelector(({ state }) => state.popup);
  const body = popup[props.popupLayer].body;

  return (
    <div className='flexColumn alignItemsCenter'>
      { body }
    </div>
  );
}

// CONFIRMATION POPUP
function ConfirmationPopupContent(props) {
  const message = props.message;
  const buttonFaceOk = props.buttonFaceOk;
  const buttonFaceCancel = props.buttonFaceCancel;
  const callbackOk = props.callbackOk;
  const callbackCancel = props.callbackCancel;

  return (
    <div className='flexColumn alignItemsCenter'>
      <div className='flexColumn alignItemsCenter margin1 '>
        { message }
      </div>
      <div className='flexRow justifyContentCenter margin1 '>
        <button className='button2 margin1 ' onClick={ callbackOk } >
          { buttonFaceOk }
        </button>
        <button className='button2 margin1 ' onClick={ callbackCancel } >
          { buttonFaceCancel }
        </button>
      </div>
    </div>
  );
}

function ConfirmationPopup(props) {
  const { state } = useGlobalStateSelector(({ state }) => ({ state }));
  const popupLayer = props.popupLayer;
  const message = state.popup[popupLayer].body.message;
  const buttonFaceOk = state.popup[popupLayer].body.buttonFaceOk;
  const buttonFaceCancel = state.popup[popupLayer].body.buttonFaceCancel;
  const callbackOk = state.popup[popupLayer].body.callbackOk;
  const callbackCancel = state.popup[popupLayer].body.callbackCancel;

  return (
    <ConfirmationPopupContent
      message={message}
      buttonFaceOk={buttonFaceOk}
      buttonFaceCancel={buttonFaceCancel}
      callbackOk={callbackOk}
      callbackCancel={callbackCancel}
    />
  );
}

// GENERAL OPTION
function GeneralOption(props) {
  const { dispatch } = useGlobalStateSelector(({ dispatch }) => ({ dispatch }));
  const record = props.record;
  const recordKey = props.recordKey;
  const sortedKeys = props.sortedKeys;
  const stateKeys = props.stateKeys;
  const layer = props.layer;
  let value;

  if (Object.keys(record).length === 1) {
    if (Object.values(record)[0].value !== undefined) {
      value = Object.values(record)[0].value;
    }
    else {
      value = Object.keys(record)[0];
    }
  }
  else {
    if (record[recordKey].value !== undefined) {
      value = record[recordKey].value;
    }
    else {
      value = recordKey;
    }
  }

  const factors = sortedKeys.map( key => {
    if (record[key].face !== undefined) {
      return <div className={record[key].style} >
        { record[key].face }
      </div>;
    }
    else {
      return <div className={record[key].style} >
        { key }
      </div>;
    }
  });


  return (
    <div className=''>
      {/* PC */}
      <button className='visibleMiddleOrMore boxMonacotto1 margin0p5 cursor riseOut2'
        onClick={ () => {
          dispatch({ type: 'setStateMultiLayers', keys: stateKeys, value: value });
          dispatch({ type: 'setStateMultiLayers', keys: ['popup', layer], value: { type: null, body: null } });
        }} 
      >
        { factors }
      </button>
      {/* SP */}
      <button className='visibleSmallOrLess boxMonacotto1SmallScreen cursor riseOut2'
        onClick={ () => {
          dispatch({ type: 'setStateMultiLayers', keys: stateKeys, value: value });
          dispatch({ type: 'setStateMultiLayers', keys: ['popup', layer], value: { type: null, body: null } });
        }} 
      >
        { factors }
      </button>
    </div>
  );
}

// FOR DEVELOPMENT
function ForDevelopment() {
  const state = useGlobalStateSelector(({ state }) => state);

  return (
    <div className='invisibleSp' >
        <hr/>
        <div className='widthVW'>
          {'<<active>> ' + JSON.stringify(state.active)}<br/>
          {'<<session>> ' + JSON.stringify(state.session)}<br/>
          {'<<configure>> ' + JSON.stringify(state.configure)}<br/>
          {'<<balance>> ' + JSON.stringify(state.balance)}<br/>
          {'<<assetInfo>> ' + JSON.stringify(state.assetInfo)}<br/>
          {'<<monacard>> ' + JSON.stringify(state.monacard)}<br/>
          {'<<config>> ' + JSON.stringify(state.config)}<br/>
          {'<<configMP>> ' + JSON.stringify(state.configMP)}<br/>
          {'<<popup>> ' + JSON.stringify(state.popup)}<br/>
        </div>
    </div>
  );
}

// TEXT AREA MULTI LAYERS STATE
function TextArea2(props) {
  const keys = props.keys;
  const fieldName = props.fieldName;
  const outerClass = props.outerClass !== undefined && props.outerClass !== null ? props.outerClass : '';
  const boxClass = props.boxClass !== undefined && props.boxClass !== null ? props.boxClass : '';
  const textAreaClass = props.textAreaClass !== undefined && props.textAreaClass !== null ? props.textAreaClass : '';
  const type = props.type !== undefined ? props.type : 'setStateMultiLayers';
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const value = useGlobalStateSelector( ({ state }) => keys.reduce( (acc, cur) => { return acc[cur]; }, state ) );

  const [draft, setDraft] = useState(value);
  const composingRef = useRef(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = (e) => {
    dispatch({type: type, keys: keys, value: e.target.value});
  };


  return (
    <div className={'flexColumn alignItemsCenter ' + outerClass}>
            <input className='invisibleCheckBox' id={'UVCB_' + fieldName + 'Background'} type='checkbox' />
            <div className={boxClass}>
              <textarea className={textAreaClass} value={draft}
                onFocus={ () => {
                  document.getElementById('UVCB_' + fieldName + 'Background').checked=true; 
                  document.getElementById('UVCB_' + fieldName + 'Label').checked=true; 
                }}
                onBlur={ () => {
                  document.getElementById('UVCB_' + fieldName + 'Background').checked=false; 
                  document.getElementById('UVCB_' + fieldName + 'Label').checked=false; 
                }}
                onCompositionStart={ () => {
                  composingRef.current = true;
                }}
                onChange={(e) => {
                  // ドラフトは“無加工”で追従（変換途中を壊さない）
                  setDraft(e.currentTarget.value);

                  // もし「IMEじゃない時は即commitしたい」ならここで:
                  if (!composingRef.current) {
                    commit(e);
                  }
                }}
                onCompositionEnd={ (e) => {
                  composingRef.current = false;
                  commit(e);
                }}
              />
              <input className='invisibleCheckBox' id={'UVCB_' + fieldName + 'Label'} type='checkbox' />
              <div className={ value === '' ? 'label1Unfilled' : 'label1Filled'}>
                {props.face}
              </div>
            </div>
    </div>
  );
}

// TEXT AREA AUTO SIZE
function TextAreaAutoSize(props) {
  const state = useGlobalStateSelector( ({ state }) => state );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const value = props.keys.reduce( (acc, cur) => { return acc[cur]; }, state );
  const type = props.type !== undefined ? props.type : 'setStateMultiLayers';
  let textAreaStyle = props.textAreaStyle !== undefined ? props.textAreaStyle : {};
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);
  const composingRef = useRef(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  if (focused === true) {
     textAreaStyle = {
       ...textAreaStyle,
       borderLeft: '3px solid var(--monacotto-color)',
     };
  }

  const commit = (targetValue) => {
    dispatch({type: type, keys: props.keys, value: targetValue});

    if (props.extraFunctionsOnChange !== undefined) {
      for (const extraFunction of props.extraFunctionsOnChange) {
        const argumentsRevised = [...extraFunction.arguments];

        if (extraFunction.targetValue !== undefined) {
          argumentsRevised.splice(extraFunction.targetValue === true ? argumentsRevised.length : extraFunction.targetValue, 0, targetValue);
        }

        extraFunction.function(...argumentsRevised);
      }
    }
  };

  return (
    <div className={'flexColumn alignItemsCenter ' + props.outerClass}>
              <TextareaAutosize className={props.textAreaClass} style={textAreaStyle} value={draft}
                minRows={props.minRows}
                onFocus={ () => {
                  setFocused(true);
                }}
                onBlur={ () => {
                  setFocused(false);
                }}
                onChange={ (e) => {
                  setDraft(e.currentTarget.value);

                  if (!composingRef.current) {
                    commit(e.currentTarget.value);
                  }
                }}
                onCompositionStart={ () => {
                  composingRef.current = true;
                }}
                onCompositionEnd={ (e) => {
                  composingRef.current = false;
                  commit(e.currentTarget.value);
                }}
              />
    </div>
  );
}

// NOTIFICATION FADING OUT
function NotificationFadingOut() {
  const { state, dispatch } = useGlobalStateSelector(({ state, dispatch }) => ({ state, dispatch }));

  useEffect( () => {
    return () => {
      dispatch({ type: 'stopNotificationAnimation', key: 'notification' });
    };
  }, []);

  return (
    <CSSTransition in={state.notification.inAnimation} timeout={5000} classNames="messageFade"
      onEntered={ () => {
                    dispatch({ type: 'stopNotificationAnimation', key: 'notification' });
                  }
               }
    >
      <div className={state.notification.inAnimation ? 'maxWidth98vw' : 'invisible'} >
          { state.notification.body[state.notification.index] }
      </div>
    </CSSTransition>
  );
}

// ROLLING PEARL
function RollingPearl() {
  const state = useGlobalStateSelector(({ state }) => state);

  let magic;

  if (state.accessing) {
    magic = <div className='pearlRollingCenter' />;
  }
  else {
    magic = null;
  }

  return (
    <div>
      { magic }
    </div>
  );
}

// TEXT LINE MULTI LAYERS STATE ON PRESS KEY
function TextLine3(props) {
  const state = useGlobalStateSelector( ({ state }) => state );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const type = props.type !== undefined ? props.type : 'setStateMultiLayers';
  const keys = props.keys;

  const value = useMemo(() => {
    let val = keys.reduce( (acc, cur) => { return acc[cur]; }, state );
    if (type === 'setStateMultiLayersFloat' || type === 'setStateMultiLayersMasked') {
      val = val.face;
    }
    return val;
  }, [keys, state, type]);

  const [draft, setDraft] = useState(value);
  const composingRef = useRef(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  let boxClass;

  if (props.boxClass !== undefined) {
    boxClass = props.boxClass;
  }
  else {
    boxClass = 'box1 widthWithButton1NarrowSp';
  }     

  let textLineClass;

  if (props.textLineClass !== undefined) {
    textLineClass = props.textLineClass;
  }
  else {
    textLineClass = 'textLine1';
  }     

  let tooltipClass;

  if (props.tooltip === 'left') {
    tooltipClass = 'tooltipLeft breakAll'
  }
  else if (props.tooltip === 'leftLong') {
    tooltipClass = 'tooltipLeft'
  }
  else if (props.tooltip === 'right') {
    tooltipClass = 'tooltipRight breakAll'
  }
  else if (props.tooltip === 'rightLong') {
    tooltipClass = 'tooltipRight'
  }
  else if (props.tooltip === false) {
    tooltipClass = 'invisible'
  }
  else if (props.tooltip !== undefined) {
    tooltipClass = 'tooltipRight'
  }
  else {
    tooltipClass = 'invisible'
  }

  let disabled;

  if (props.disabled) {
    disabled = true;
  }
  else {
    disabled = false;
  }

  const commit = (e) => {
    let targetValueAdjusted = e.target.value;

    if (type === 'setStateMultiLayersFloat') {
      targetValueAdjusted = adjustFloat(props.adjustType, e.target.value, props.adjustExp);
    }

    dispatch({type: type, keys: keys, value: e.target.value, adjustType: props.adjustType, adjustExp: props.adjustExp});

    if (props.extraDispatch !== undefined) {
      for (const aDispatch of props.extraDispatch) {
        dispatch({type: aDispatch.type, keys: aDispatch.keys, value: e.target.value});
      }
    }

    if (props.extraFunctionOnChange !== undefined) {
      for (const aFunction of props.extraFunctionOnChange) {
        const argumentsRevised = [...aFunction.arguments];

        if (aFunction.targetValue !== undefined) {
          argumentsRevised.splice(aFunction.targetValue === true ? argumentsRevised.length : aFunction.targetValue, 0, targetValueAdjusted);
        }

        devLog('argumentsRevised', argumentsRevised);
        aFunction.function(...argumentsRevised);
      }
    }
  };

  return (
    <div>
            <input className='invisibleCheckBox' id={'UVCB_' + props.fieldName + 'Background'} type='checkbox' />
            <div className={boxClass}
              onMouseEnter={ () => {
                if (props.tooltip !== undefined && value !== undefined && value !== null && value !== '') {
                  document.getElementById('UVCB_' + props.fieldName + 'Tooltip').checked=true; 
                }
              }}
              onMouseLeave={ () => {
                if (props.tooltip !== undefined) {
                  document.getElementById('UVCB_' + props.fieldName + 'Tooltip').checked=false; 
                }
              }}
            >
              <input className={textLineClass} type="text" value={draft} placeholder='' disabled={disabled}
                onFocus={ () => {
                  document.getElementById('UVCB_' + props.fieldName + 'Background').checked=true; 
                  document.getElementById('UVCB_' + props.fieldName + 'Label').checked=true; 

                  if (props.tooltip !== undefined) {
                    document.getElementById('UVCB_' + props.fieldName + 'Tooltip').checked=false; 
                  }
                }}
                onBlur={ () => {
                  document.getElementById('UVCB_' + props.fieldName + 'Background').checked=false; 
                  document.getElementById('UVCB_' + props.fieldName + 'Label').checked=false; 

                  if (type === 'setStateMultiLayersFloat') {
                    dispatch({type: 'adjustFace', keys: props.keys});
                  }
                }}
                onCompositionStart={ () => {
                  composingRef.current = true;
                }}
                onChange={(e) => {
                  // ドラフトは“無加工”で追従（変換途中を壊さない）
                  setDraft(e.currentTarget.value);

                  // もし「IMEじゃない時は即commitしたい」ならここで:
                  if (!composingRef.current) {
                    commit(e);
                  }

                  // 入力中はtooltipが邪魔なので非表示にする
                  if (props.tooltip !== undefined) {
                    document.getElementById('UVCB_' + props.fieldName + 'Tooltip').checked=false; 
                  }
                }}
                onCompositionEnd={ (e) => {
                  composingRef.current = false;
                  commit(e);
                }}
                onKeyPress={ (e) => {
                  if (e.which === props.keyNo) {
                    if (props.onKeyPressFunction !== undefined) {
                      props.onKeyPressFunction();
                    }
                  }
                }}
              />
              <input className='invisibleCheckBox' id={'UVCB_' + props.fieldName + 'Label'} type='checkbox' />
              <div className={ value === undefined || value === '' || value === null ? 'label1Unfilled' : 'label1Filled'}>
                {props.face}
              </div>
              <input className='invisibleCheckBox' id={'UVCB_' + props.fieldName + 'Tooltip'} type='checkbox' />
              <div className={tooltipClass} >
                {value}
              </div>
            </div>
    </div>
  );
}

// QR CODE SCANNER
function QrCodeScanner(props) {
  const state = useGlobalStateSelector( ({ state }) => state );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  // const [resultData, setResultData] = useState(undefined);
  const popupLayer = props.popupLayer;
  const callback = props.callback;
  const callbackType = props.callbackType;
  const options = props.options;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  devLog('QrCodeScanner rendered.');

  useEffect( () => {
    devLog('useEffect triggered.');
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 300 },
        height: { ideal: 300 },
      },
    };

    // デバイスのカメラにアクセスする
    navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      // デバイスのカメラにアクセスすることに成功したら、video要素にストリームをセットする
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        scanQrCode();
      }
    })
    .catch((err) => console.error('Error accessing media devices:', err));

    const currentVideoRef = videoRef.current

    // コンポーネントがアンマウントされたら、カメラのストリームを停止する
    return () => {
      devLog('cleanup');
      if (currentVideoRef && currentVideoRef.srcObject) {
        const stream = currentVideoRef.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  const scanQrCode = () => {
    devLog('scanQrCode');
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (canvas && video) {
      const ctx = canvas.getContext('2d');
      // canvas.width = video.videoWidth;
      // canvas.height = video.videoHeight;

      if (ctx) {
        // カメラの映像をcanvasに描画する
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // QRコードをスキャンする
        const qrCodeData = jsQR(imageData.data, imageData.width, imageData.height);

        if (qrCodeData) {
          // スキャンされた内容を確認する
          // if (false) {
          //   setTimeout(scanQrCode, 100); // スキャンの頻度を制限
          //   return;
          // }

          // setResultData(qrCodeData.data)
          dispatch({ type: 'setStateMultiLayers', keys: ['qrCodeScanner', 'qrCode'], value: qrCodeData.data });
          dispatch({ type: 'setNotification', key: 'notification', value: words.scaned[state.language] });
          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });

          if (callbackType === 'decryptMnemonic') {
            doSomethingWithEncryptedMnemonics(state, dispatch, [qrCodeData.data], callback, options);
          }
          else { // simpelCallback
            callback(qrCodeData.data);
          }

          return;
        }

        setTimeout(scanQrCode, 100);
      }
    }
  };

  // let scanner;

  // if (resultData === undefined) {
  //   scanner =
  //   <div className=''>
  //     <div className=''>
  //       <video ref={videoRef} autoPlay playsInline className='' />
  //       <canvas ref={canvasRef} width='300' height='300' className='' />
  //     </div>
  //   </div>;
  // }

  // let button;
  // 
  // if (resultData !== undefined) {
  //   button =
  //   <div className=''>
  //     <button>push</button>
  //   </div>
  // }


  return (
    <div className='flexColumn justifyContentCenter alignItemsCenter relative border qrScannerFrame'>
      <video ref={videoRef} autoPlay playsInline className='absoluteZero zM10 qrScannerVideo' />
      <canvas ref={canvasRef} width='300' height='300' className='qrScannerCanvas' />
    </div>
  );
}

// QR CODE READER
function QrCodeReader(props) {
  const language = useGlobalStateSelector( ({ state }) => state.language || 'english' );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const popupLayer = props.popupLayer;
  const closePopup = props.closePopup ?? true;
  const resolve = props.resolve;
  const reject = props.reject;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  devLog('QrCodeReader rendered.');

  useEffect( () => {
    devLog('useEffect triggered.');
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 300 },
        height: { ideal: 300 },
      },
    };

    // デバイスのカメラにアクセスする
    navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      // デバイスのカメラにアクセスすることに成功したら、video要素にストリームをセットする
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        scanQrCode();
      }
    })
    .catch((err) => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.cannotAccessCamera[language] });
      reject(new Error('cannotAccessCamera'));
    });

    const currentVideoRef = videoRef.current

    // コンポーネントがアンマウントされたら、カメラのストリームを停止する
    return () => {
      devLog('cleanup');
      if (currentVideoRef && currentVideoRef.srcObject) {
        const stream = currentVideoRef.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);

  const scanQrCode = () => {
    devLog('scanQrCode');
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (canvas && video) {
      const ctx = canvas.getContext('2d');
      // canvas.width = video.videoWidth;
      // canvas.height = video.videoHeight;

      if (ctx) {
        let qrCodeData;

        try {
          // カメラの映像をcanvasに描画する
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          // QRコードをスキャンする
          qrCodeData = jsQR(imageData.data, imageData.width, imageData.height);
        }
        catch (error) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.cannotReadDataFromCamera[language] });
          reject(new Error('cannotReadDataFromCamera'));
        }

        if (qrCodeData) {
          // スキャンされた内容を確認する
          // if (false) {
          //   setTimeout(scanQrCode, 100); // スキャンの頻度を制限
          //   return;
          // }

          dispatch({ type: 'setStateMultiLayers', keys: ['qrCodeScanner', 'qrCode'], value: qrCodeData.data });
          dispatch({ type: 'setNotification', key: 'notification', value: words.scaned[language] });

          if (closePopup) {
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });
          }

          resolve(qrCodeData.data);
        }

        setTimeout(scanQrCode, 100);
      }
    }
  };


  return (
    <div className='flexColumn justifyContentCenter alignItemsCenter relative border qrScannerFrame'>
      <video ref={videoRef} autoPlay playsInline className='absoluteZero zM10 qrScannerVideo' />
      <canvas ref={canvasRef} width='300' height='300' className='qrScannerCanvas' />
    </div>
  );
}

// DISPLAY QR POPUP
function DisplayQrPopup(props) {
  const popupLayer = props.popupLayer;
  const popup = useGlobalStateSelector(({ state }) => state.popup);
  const language = useGlobalStateSelector(({ state }) => state.language);
  const data = popup[popupLayer].body.data;
  const fileNameArg = popup[popupLayer].body.fileName;
  const canvasRef = useRef(null);
  const canvasId = `qrCodeCanvas-${popupLayer}`;
  const now = new Date();

  let fileName;
  if (fileNameArg !== undefined && fileNameArg !== null && fileNameArg !== '') {
    fileName = fileNameArg;
  }
  else {
    fileName = `QRCode-${String(localTime(now.getTime(), 'digit14Num'))}.png`;
  }

  const downloadPng = () => {
    const node = canvasRef.current;
    const canvas =
      (node?.tagName === 'CANVAS' ? node : node?.querySelector?.('canvas')) ||
      document.getElementById(canvasId);
    if (!canvas) return;

    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };


  return (
    <div className='flexColumn alignItemsCenter '>
      <QRCodeCanvas
        ref={canvasRef}
        id={canvasId}
        value={data}
        size={300}
        includeMargin
      />
      <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter marginTop1 ' tabindex='0'
        onClick={downloadPng}
      >
        { words.download[language] }
      </button>
    </div>
  );
}

// QR CODE SCANNER POPUP ; type: 'qrCodeScannerPopup'
function QrCodeScannerPopup(props) {
  const state = useGlobalStateSelector(({ state }) => state);
  const popupLayer = props.popupLayer;
  const body = state.popup[popupLayer]?.body ?? {};

  return (
    <div className='flexColumn alignItemsCenter widthMax'>
      <QrCodeScanner
        popupLayer={popupLayer}
        callback={body.callback}
        callbackType={body.callbackType}
        options={body.options}
      />
    </div>
  );
}

// QR CODE READER POPUP ; type: 'qrCodeReaderPopup'
function QrCodeReaderPopup(props) {
  const popupLayer = props.popupLayer;
  const popup = useGlobalStateSelector(({ state }) => state.popup[popupLayer]);
  const closePopup = popup?.body?.closePopup ?? true;
  const resolve = popup?.body?.resolve;
  const reject = popup?.body?.reject;

  return (
    <div className='flexColumn alignItemsCenter widthMax'>
      <QrCodeReader
        popupLayer={popupLayer}
        closePopup={closePopup}
        resolve={resolve}
        reject={reject}
      />
    </div>
  );
}

export {
  ClipboardCopyButton,
  GeneralList,
  GeneralItems,
  GeneralOption,
  ConfirmationPopupContent,
  ConfirmationPopup,
  ForDevelopment,
  NotificationFadingOut,
  RollingPearl,
  DisplayQrPopup,
  TextLine3,
  TextArea2,
  TextAreaAutoSize,
  QrCodeScanner,
  QrCodeScannerPopup,
  QrCodeReader,
  QrCodeReaderPopup,
};
